import {Alert, Box, useAccount, useListingsByAccountID, VStack} from "@hosttools/frontend";
import type {AccountType} from "@hosttools/frontend/models/account";
import {type Listing} from "@hosttools/frontend/models/listing";
import type {AccountListing} from "@hosttools/frontend/services/listing";
import {createColumnHelper, type Row} from "@tanstack/react-table";
import {type FC, memo, type ReactNode, useCallback, useMemo, useRef, useState} from "react";

import useFetchListingsFromAccount from "../useFetchListingsFromAccount";

import Header from "./Header";
import TableActionColumn from "./TableActionColumn";
import TableThumbnailColumn from "./TableThumbnailColumn";

import Loader from "@/admin/components/Loader";
import ModalConnectChannels from "@/client/components/ModalConnectChannels";
import TableFetch, {type RefValue as TableRefValue} from "@/client/components/TableFetch";
import TableCellTextColumn from "@/client/components/TableFetch/TableCellTextColumn";
import TableCheckBoxColumn from "@/client/components/TableFetch/TableCheckBoxColumn";
import TableCheckBoxHeader from "@/client/components/TableFetch/TableCheckBoxHeader";

interface BaseProps {
    accountID: string;
}

interface ImportListingsProps extends BaseProps {
    type: "importListings";
    onSelectListings: (ids: string[], accountID: string) => void;
}

interface ConnectChannelsProps extends BaseProps {
    type: "connectChannels";
    onToggleChannel?: (listing: Listing, accountType: AccountType, isConnected: boolean) => void;
}

interface MatchListingProps extends BaseProps {
    type: "matchListing";
    onConnect: (accountID: string, externalListingID: string) => Promise<void>;
}

type Props = StrictUnion<ImportListingsProps | ConnectChannelsProps | MatchListingProps>;

const TableAccountListings: FC<Props> = ({
    accountID,
    type,
    onSelectListings,
    onToggleChannel,
    onConnect
}) => {
    const tableRef = useRef<TableRefValue<Listing | AccountListing>>(null);
    const columnHelper = useMemo(() => createColumnHelper<Listing | AccountListing>(), []);
    const [connectingListing, setConnectingListing] = useState<Listing>();

    const handleCloseModal = useCallback(() => {
        setConnectingListing(undefined);
    }, []);

    const columns = useMemo(
        () =>
            [
                type === "importListings"
                    ? columnHelper.accessor(row => row._id, {
                          id: "select",
                          enableSorting: false,
                          header: TableCheckBoxHeader,
                          cell: TableCheckBoxColumn
                      })
                    : undefined,
                columnHelper.accessor(row => row.airbnbThumbnailUrl, {
                    id: "name",
                    header: "Name",
                    cell: TableThumbnailColumn
                }),
                columnHelper.accessor(row => row.airbnbAddress ?? "-", {
                    id: "address",
                    header: "Address",
                    cell: TableCellTextColumn
                }),
                type === "connectChannels"
                    ? columnHelper.display({
                          id: "action",
                          header: "Connected channels",
                          cell: ({row: {original}}) =>
                              (
                                  <TableActionColumn
                                      listing={original}
                                      type={type}
                                      onPress={setConnectingListing}
                                  />
                              ) as ReactNode
                      })
                    : type === "matchListing"
                    ? columnHelper.display({
                          id: "action",
                          header: "",
                          cell: ({row: {original}}) =>
                              (
                                  <TableActionColumn
                                      accountID={accountID}
                                      listing={original as AccountListing}
                                      type={type}
                                      onConnect={onConnect}
                                  />
                              ) as ReactNode
                      })
                    : undefined
            ].filter(Boolean),
        [type, accountID, columnHelper, onConnect]
    );

    // no fetch in case of importing
    const {data: listingsFromAccount, isError} = useFetchListingsFromAccount(
        type === "importListings" || type === "matchListing" ? accountID : undefined
    );
    const account = useAccount(accountID);
    const connectedListings = useListingsByAccountID(accountID);

    const handleStateChange = useCallback(() => {
        const table = tableRef.current?.table;
        if (table && type === "importListings") {
            const {rowSelection} = table.getState();
            const ids = Object.keys(rowSelection);
            onSelectListings(ids, accountID);
        }
    }, [type, accountID, onSelectListings]);

    const data =
        type === "importListings" || type === "matchListing"
            ? listingsFromAccount
            : connectedListings;

    const isRowSelectionEnabled = useCallback(({original}: Row<Listing | AccountListing>) => {
        return "isConnected" in original ? !original.isConnected : true;
    }, []);

    if (!account) {
        return null;
    }

    return (
        <Box>
            {/*
                DON'T put any modal under a VStack that has `space` property which ends up creating more spacer
                as soon as the modal shows
            */}
            <VStack
                className={`${
                    isError ? "" : "min-h-[160px]"
                } relative p-4 rounded-lg bg-blueGray-50`}
                space="lg"
            >
                <Header account={account} />
                {!data ? (
                    isError ? (
                        <Alert action="error">Failed to load listings for this account</Alert>
                    ) : (
                        <Loader center>Fetching listings...</Loader>
                    )
                ) : (
                    <TableFetch
                        ref={tableRef}
                        columns={columns}
                        data={data}
                        enableGlobalFilter={false}
                        enableRowSelection={isRowSelectionEnabled}
                        gridTemplateColumnsClass={
                            type === "importListings"
                                ? "grid-cols-[minmax(64px,_auto)_minmax(320px,_1fr)_minmax(200px,_auto)]"
                                : "grid-cols-[minmax(300px,_1fr)_minmax(200px,_auto)_minmax(min-content,_auto)]"
                        }
                        mode="offline"
                        rowId="_id"
                        testID="import-listings"
                        onStateChange={handleStateChange}
                    />
                )}
            </VStack>
            {type === "connectChannels" && connectingListing && (
                <ModalConnectChannels
                    listing={connectingListing}
                    onClose={handleCloseModal}
                    onToggleChannel={onToggleChannel}
                />
            )}
        </Box>
    );
};

export default memo(TableAccountListings);
